import { createRouter, createWebHashHistory, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router';
import { ElMessage } from 'element-plus';
import { hasPerm, isPlatformSuperAdmin, session } from '@/core';
import { routes } from './routes';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import { usePermissionStore, wirePermissionContext } from '@/stores/permission';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    crumb?: string;
    /** 菜单级权限码 */
    perm?: string;
    menu?: boolean;
    menuOrder?: number;
    activeMenu?: string;
    public?: boolean;
  }
}

/** 管理端与交付后台统一约定：hash 路由（便于静态托管与端隔离部署，无需服务端 rewrite） */
export const router = createRouter({
  history: createWebHashHistory(import.meta.env.VITE_PUBLIC_PATH || '/'),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

wirePermissionContext();

function isPublic(route: RouteLocationNormalized): boolean {
  return route.matched.some((record) => record.meta.public === true);
}

/**
 * t122（首登强制改密）：判据是**真信号** `user.forcePwdChange`（后端登录响应第 5 键；
 * 归一后随会话快照持久化 ⇒ 刷新页面后拦截仍在）。
 * ⛔ 不看 `status`：`PASSWORD_CHANGE_REQUIRED` 后端从不下发（t98/t108 全仓 0 命中）⇒ 那类分支是死码。
 */
function needsPasswordChange(): boolean {
  return session.getSessionUser()?.forcePwdChange === true;
}

/**
 * 登录后的落地页 —— 与侧边栏同一份判据（`meta.menu` + `hasPerm` + `menuOrder`），⛔ 写死某一屏。
 *
 * 实测缺陷（5198 群主官方档）：落地页原先写死 `/couriers`，而那屏 `meta.perm` 是 `user:view`；
 * 交付机构管理员 wang.ops 没有这枚权限，登录成功后第一屏就被守卫弹成
 * `#/forbidden?reason=perm&perm=user:view`。写死任何一屏都会随角色权限漂移复发。
 */
export function loginLandingPath(): string {
  const layout = routes.find((r) => r.path === '/') as { children?: RouteRecordRaw[] } | undefined;
  const menus = (layout?.children ?? [])
    .filter((r) => r.meta?.menu === true && hasPerm(r.meta.perm))
    .sort((a, b) => (a.meta?.menuOrder ?? 99) - (b.meta?.menuOrder ?? 99));
  const first = menus[0];
  return first?.path ? `/${first.path}` : '/orders';
}

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const app = useAppStore();
  const permission = usePermissionStore();

  if (isPublic(to)) {
    if (to.name === 'login' && auth.isAuthenticated) return { path: '/orders' };
    return true;
  }

  if (!session.getAccessToken()) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  if (!auth.user) auth.restore();
  if (!auth.user) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  // 会话 30min 超时强制登出
  if (auth.expiresAt && auth.expiresAt < Date.now()) {
    ElMessage.warning('会话已超时，请重新登录');
    auth.forceLogout();
    return { path: '/login', query: { redirect: to.fullPath, reason: 'timeout' } };
  }

  // §6.2-7：刷新后先按 profile 重建权限态再判权限；端点不可用时沿用登录时服务端签发的 interim 快照（state=stale）
  if (auth.snapshotState === 'pending') await auth.syncProfile();

  // 第8条（用户 m00163）：本端只服务交付侧身份 —— 平台超管在启动自检这一步就被明确拒绝，
  // 同时清掉改前可能已落在本地的会话（令牌＋快照），⛔ 不放行到业务屏再靠菜单/按钮判据「看不到东西」。
  if (isPlatformSuperAdmin(auth.user)) {
    void auth.logout(false);
    return { path: '/login', query: { redirect: to.fullPath, reason: 'super-admin' } };
  }

  // 跨端登录隔离（用户 m02355）：平台控制台账号只属于 5173。登录层已按 X-Client-End 拒绝，
  // 这里再对「旧令牌/绕过登录口」的会话做启动自检，清令牌与快照后回登录页。
  if (auth.user?.console === 'ADMIN') {
    void auth.logout(false);
    return { path: '/login', query: { redirect: to.fullPath, reason: 'platform-side' } };
  }

  // t122（首登强制改密）：首登位在场 ⇒ 只放行改密页，其余业务视图一律阻断
  if (needsPasswordChange()) {
    if (to.name === 'changePassword') return true;
    return { path: '/change-password', query: { redirect: to.fullPath } };
  }

  // 端绑定互斥：外部角色（BPO客服/交付专员）禁止进入管理端
  if (!permission.endAllowed) {
    return { path: '/forbidden', query: { reason: 'end-binding' } };
  }

  // 菜单级权限
  if (to.meta.perm && !hasPerm(to.meta.perm)) {
    return { path: '/forbidden', query: { reason: 'perm', perm: to.meta.perm } };
  }

  app.setCrumb(to.meta.crumb || to.meta.title || '');
  return true;
});

router.afterEach((to) => {
  const title = to.meta.title ? `${to.meta.title} · 营销交付中控系统` : '营销交付中控系统';
  if (document.title !== title) document.title = title;
});
